import {
  selectRetailerAreaNames,
  selectIsRetailerAreaNamesLoading,
  selectRetailerParents,
} from "./../../../store/data-exchange.selectors";
import { select, Store } from "@ngrx/store";
import { IDropdownOption } from "@app/models/idropdown-option";
import { SubscriptionFilterChangedEvent } from "./../../../models/subscription-filter-changed-event";
import { SupplierDcBrandFilterComponent } from "./../supplier-dc-brand-filter/supplier-dc-brand-filter.component";
import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { CreateSubscriptionFilterComponent } from "../../../models/create-subscription-filter-component";
import { SubscriptionFilter } from "../../../models/subscription-filter";
import { RetailerArea } from "../../../models/retailer-area";
import { SubscriptionFilterKey } from "../../../models/enum/subscription-filter-key";
import { takeUntil } from "rxjs/operators";
import { DestroyableDirective } from "@app/abstract/destroyable";
import { selectedRetailerParentListChanged } from "../../../store/data-exchange.actions";

export const channels: IDropdownOption[] = [
  { label: "Airlines, Airports & Caterers", value: "AirlinesAirportsCaterers" },
  { label: "Beer, Wine & Spirits", value: "BeerWineSpirits" },
  { label: "Convenience", value: "Convenience" },
  { label: "Dollar / Discount", value: "DollarDiscount" },
  { label: "Drug", value: "Drug" },
  { label: "E-Commerce / E-Grocery", value: "ECommerceEGrocery" },
  { label: "Foodservice", value: "Foodservice" },
  { label: "Grocery - Chain (National)", value: "GroceryChainNational" },
  { label: "Grocery - Independent", value: "GroceryIndependent" },
  { label: "Grocery - Regional", value: "GroceryRegional" },
  { label: "Mass / Club", value: "MassClub" },
  { label: "Other Non-Retail", value: "OtherNonRetail" },
  { label: "Other Retail", value: "OtherRetail" },
  { label: "Other Travel & Entertainment", value: "OtherTravelEntertainment" },
  { label: "Resellers", value: "Resellers" },
  { label: "Wholesalers & Distributors", value: "WholesalersDistributors" },
  { label: "DPI History", value: "DPIHistory" },
];

@Component({
  selector: "app-kehe-full-pod-filter",
  templateUrl: "./kehe-full-pod-filter.component.html",
  styleUrls: [],
  standalone: false,
})
export class KeheFullPodFilterComponent
  extends DestroyableDirective
  implements OnInit, CreateSubscriptionFilterComponent
{
  retailerParentOptions: RetailerArea[] = [];
  selectedRetailerParentList: RetailerArea[] = [];
  retailerAreaNameOptions: RetailerArea[] = [];
  selectedRetailerAreaList: RetailerArea[] = [];
  supplierDcBrandState: SubscriptionFilterChangedEvent = {
    filtersValid: false,
    selectedSupplier: null,
    filters: [],
  };
  channelOptions: IDropdownOption[] = channels.sort((a, b) =>
    a.label.localeCompare(b.label),
  );
  selectedChannelList: IDropdownOption[] = [];

  @ViewChild("suppierDcBrandFilter", { static: true })
  supplierDcBrandFilter: SupplierDcBrandFilterComponent;

  isRetailerAreaNamesLoading$ = this._store.select(
    selectIsRetailerAreaNamesLoading,
  );

  @Output()
  filterChanged = new EventEmitter<SubscriptionFilterChangedEvent>();

  constructor(private _store: Store) {
    super();
  }

  ngOnInit() {
    this._store
      .pipe(takeUntil(this.destroy$), select(selectRetailerParents))
      .subscribe((parents) => {
        this.retailerParentOptions = parents;
      });

    this._store
      .pipe(takeUntil(this.destroy$), select(selectRetailerAreaNames))
      .subscribe((names) => {
        this.retailerAreaNameOptions = names;
        this.selectedRetailerAreaList = this.selectedRetailerAreaList.filter(
          (selectedArea) => names.some((name) => name.id === selectedArea.id),
        );
      });
  }

  onChannelChanged() {
    this.publishFilterChangedEvent();
  }

  onSupplierDcBrandChanged(event: SubscriptionFilterChangedEvent): void {
    this.supplierDcBrandState = event;
    this.publishFilterChangedEvent();
  }

  onChainGroupSelected() {
    this.publishFilterChangedEvent();
  }

  selectAllRetailerAreas(): void {
    if (
      this.selectedRetailerParentList?.length === 0 ||
      this.selectedRetailerAreaList.length ===
        this.retailerAreaNameOptions.length
    ) {
      return;
    }
    this.selectedRetailerAreaList = this.retailerAreaNameOptions;
    this.onChainGroupSelected();
  }

  reset(): void {
    this.selectedRetailerParentList = [];
    this.selectedRetailerAreaList = [];
    this.selectedChannelList = [];
    this.supplierDcBrandState.filtersValid = false;
    this.supplierDcBrandFilter.reset();
  }

  isFormValid(): boolean {
    return (
      this.supplierDcBrandState.filtersValid &&
      !!this.selectedRetailerParentList &&
      this.selectedRetailerParentList.length > 0 &&
      !!this.selectedRetailerAreaList &&
      this.selectedRetailerAreaList.length > 0 &&
      !!this.selectedChannelList &&
      this.selectedChannelList.length > 0
    );
  }

  getFilters(): SubscriptionFilter[] {
    return this.supplierDcBrandState.filters.concat([
      {
        key: SubscriptionFilterKey.RetailerParent,
        value: this.selectedRetailerParentList.map((ra) => ra.id.toString()),
      },
      {
        key: SubscriptionFilterKey.RetailerArea,
        value: this.selectedRetailerAreaList.map((ra) => ra.id.toString()),
      },
      {
        key: SubscriptionFilterKey.Channel,
        value: this.selectedChannelList.map((channel) => channel.value),
      },
    ]);
  }

  publishFilterChangedEvent(): void {
    const formValid = this.isFormValid();

    this.filterChanged.emit({
      filtersValid: formValid,
      selectedSupplier: this.supplierDcBrandState.selectedSupplier,
      filters: formValid ? this.getFilters() : [],
    });
  }

  tagMapper(tags: any[]): any[] {
    return tags.length < 3 ? tags : [tags];
  }

  onRetailerParentModelChange() {
    if (this.selectedRetailerParentList.length > 0) {
      this._store.dispatch(
        selectedRetailerParentListChanged({
          ids: this.selectedRetailerParentList.map((rp) => rp.id),
        }),
      );
    } else {
      this.selectedRetailerAreaList = [];
    }
    this.publishFilterChangedEvent();
  }

  selectAllRetailerParents(): void {
    if (
      this.selectedRetailerParentList.length ===
      this.retailerParentOptions.length
    ) {
      return;
    }
    this.selectedRetailerParentList = this.retailerParentOptions;
    this.onRetailerParentModelChange();
    this.publishFilterChangedEvent();
  }

  selectAllChannels(): void {
    if (this.selectedChannelList.length === this.channelOptions.length) {
      return;
    } else {
      this.selectedChannelList = this.channelOptions;
    }
    this.publishFilterChangedEvent();
  }

  onChannelSelected(): void {
    this.publishFilterChangedEvent();
  }
}
